import React, { useMemo } from 'react';
import { components } from 'react-select';
import { ChevronDown, Cross } from '@saleshandy/icons';
import MultiSelect from '../../../../../shared/design-system/components/atoms/multi-select/multi-select';
import { FilterComponentType } from '../../../types';
import { COMPANY_LIST_OPTIONS } from '../../../helpers/filter-data';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';

type Option = { value: string; label: string; count?: number };

type SavedListData = {
  id: number;
  label: string;
  visibility?: any;
  count?: number;
  isDefault?: boolean;
  owner?: {
    firstName: string;
    lastName: string;
  };
};

const DEFAULT_MAX_TOTAL_LIMIT = 50;

type Props = {
  includeKey: string;
  excludeKey: string;
  pendingFilters: URLSearchParams;
  handleQuery: (params: {
    filterKey: string;
    filterValue: any;
    filterType: string;
    filterExcludeKey?: string;
  }) => void;
  filterType?: string;
  savedListsData?: SavedListData[];
  maxTotalLimit?: number;
  isLoadingLists?: boolean;
};

const normalize = (v: string) => v.replace(/"/g, '').trim();

const toOptions = (values: string[], allOptions: Option[]) => {
  const normalizedSet = new Set(values.map(normalize));
  return allOptions.filter((o) => normalizedSet.has(o.value));
};

const getSelectedOptions = (ids: string[], allOptions: Option[]) => {
  const fromOptions = toOptions(ids, allOptions);
  const optionValues = new Set(allOptions.map((o) => o.value));
  const missingIds = ids.filter((id) => !optionValues.has(normalize(id)));
  const synthetic = missingIds.map((id) => ({
    value: id,
    label: id,
    count: 0,
  }));
  return [...fromOptions, ...synthetic];
};

const CompanyListFilter: React.FC<Props> = ({
  includeKey,
  excludeKey,
  pendingFilters,
  handleQuery,
  filterType,
  savedListsData,
  maxTotalLimit = DEFAULT_MAX_TOTAL_LIMIT,
  isLoadingLists = false,
}) => {
  const options = useMemo(() => {
    if (savedListsData && savedListsData.length > 0) {
      return savedListsData
        .filter((list) => !list.isDefault)
        ?.map((list) => ({
          value: String(list.id),
          label: `${list.label} (${list.count})`,
          count: list.count ?? 0,
        })) as Option[];
    }
    return [...COMPANY_LIST_OPTIONS] as Option[];
  }, [savedListsData]);

  const includedIds = useMemo(
    () => pendingFilters.getAll(includeKey).map(normalize),
    [pendingFilters, includeKey],
  );
  const excludedIds = useMemo(
    () => pendingFilters.getAll(excludeKey).map(normalize),
    [pendingFilters, excludeKey],
  );

  const includedSelected = useMemo(
    () => getSelectedOptions(includedIds, options),
    [includedIds, options],
  );
  const excludedSelected = useMemo(
    () => getSelectedOptions(excludedIds, options),
    [excludedIds, options],
  );

  const totalSelectedCount = includedIds.length + excludedIds.length;
  const isAtLimit = totalSelectedCount >= maxTotalLimit;

  const includeSelectOptions = useMemo(() => {
    const excludedSet = new Set(excludedIds);
    return options.filter((o) => !excludedSet.has(o.value));
  }, [options, excludedIds]);

  const excludeSelectOptions = useMemo(() => {
    const includedSet = new Set(includedIds);
    return options.filter((o) => !includedSet.has(o.value));
  }, [options, includedIds]);

  const includedSet = useMemo(() => new Set(includedIds), [includedIds]);
  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);
  const includeIsOptionDisabled = useMemo(
    () => (option: Option) =>
      option.count === 0 ||
      (isAtLimit && !includedSet.has(normalize(option.value))),
    [isAtLimit, includedSet],
  );
  const excludeIsOptionDisabled = useMemo(
    () => (option: Option) =>
      option.count === 0 ||
      (isAtLimit && !excludedSet.has(normalize(option.value))),
    [isAtLimit, excludedSet],
  );

  const includeMaxSelectable = maxTotalLimit - excludedIds.length;
  const excludeMaxSelectable = maxTotalLimit - includedIds.length;

  const onChange = ({
    newIncludes,
    newExcludes,
  }: {
    newIncludes: string[];
    newExcludes: string[];
  }) => {
    let includeSet = new Set(newIncludes.map(normalize));
    let excludeSet = new Set(newExcludes.map(normalize));

    // Ensure a list can't be both included and excluded.
    includeSet.forEach((id) => {
      if (excludeSet.has(id)) {
        excludeSet.delete(id);
      }
    });

    const totalCount = includeSet.size + excludeSet.size;
    if (totalCount > maxTotalLimit) {
      if (includeSet.size > excludeSet.size) {
        const trimCount = totalCount - maxTotalLimit;
        const includeArray = Array.from(includeSet);
        includeSet = new Set(
          includeArray.slice(0, includeArray.length - trimCount),
        );
      } else {
        const trimCount = totalCount - maxTotalLimit;
        const excludeArray = Array.from(excludeSet);
        excludeSet = new Set(
          excludeArray.slice(0, excludeArray.length - trimCount),
        );
      }
    }

    handleQuery({
      filterKey: includeKey,
      filterExcludeKey: excludeKey,
      filterType: filterType || FilterComponentType.MULTILEVEL_CHECKBOX,
      filterValue: {
        includedKeys: Array.from(includeSet),
        excludedKeys: Array.from(excludeSet),
      },
    });
  };

  const ClearIndicator = (props) => {
    const {
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <span
        {...restInnerProps}
        ref={ref}
        className="company-list-filter-clear-indicator"
      >
        <Cross color="#6B7280" />
      </span>
    );
  };

  const MultiValueRemove = (props) => {
    const {
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <span
        {...restInnerProps}
        ref={ref}
        className="company-list-filter-multi-value-remove"
      >
        <Cross color="#6B7280" height={14} width={14} />
      </span>
    );
  };

  const CheckboxOption = (props) => {
    const { isSelected, isDisabled, label } = props;
    return (
      <components.Option {...props} className="d-flex align-items-center">
        <div className="d-flex align-items-center pointer company-list-filter-option">
          <Checkbox
            checked={isSelected}
            onChange={() => null}
            value={label}
            label={
              <span className="company-list-filter-option-label">{label}</span>
            }
            disabled={isDisabled}
          />
        </div>
      </components.Option>
    );
  };

  return (
    <div className="company-list-filter">
      <div className="company-list-filter-row">
        <div className="company-list-filter-label">
          Include lists
          {isAtLimit && (
            <span className="company-list-filter-limit-msg text-muted ms-1">
              (Max {maxTotalLimit} total)
            </span>
          )}
        </div>
        <MultiSelect
          key="include"
          placeholder={
            isAtLimit
              ? `Max ${maxTotalLimit} total (${totalSelectedCount} selected)`
              : 'Select lists'
          }
          className="company-list-filter-select"
          classNamePrefix="company-list-filter-select"
          options={includeSelectOptions}
          value={includedSelected}
          isOptionDisabled={includeIsOptionDisabled}
          disabled={isLoadingLists}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onChange={(selected: any) => {
            const values = (selected ?? []).map((o: Option) => o.value);
            const cappedValues =
              !isAtLimit && values.length > includeMaxSelectable
                ? values.slice(0, includeMaxSelectable)
                : values;
            onChange({ newIncludes: cappedValues, newExcludes: excludedIds });
          }}
          components={{
            Option: CheckboxOption,
            DropdownIndicator: () => <ChevronDown color="#6B7280" />,
            ClearIndicator,
            MultiValueRemove,
          }}
        />
      </div>

      <div className="company-list-filter-row">
        <div className="company-list-filter-label">
          Exclude lists
          {isAtLimit && (
            <span className="company-list-filter-limit-msg text-muted ms-1">
              (Max {maxTotalLimit} total)
            </span>
          )}
        </div>
        <MultiSelect
          key="exclude"
          placeholder={
            isAtLimit
              ? `Max ${maxTotalLimit} total (${totalSelectedCount} selected)`
              : 'Select lists'
          }
          className="company-list-filter-select"
          classNamePrefix="company-list-filter-select"
          options={excludeSelectOptions}
          value={excludedSelected}
          isOptionDisabled={excludeIsOptionDisabled}
          disabled={isLoadingLists}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onChange={(selected: any) => {
            const values = (selected ?? []).map((o: Option) => o.value);
            const cappedValues =
              !isAtLimit && values.length > excludeMaxSelectable
                ? values.slice(0, excludeMaxSelectable)
                : values;
            onChange({ newIncludes: includedIds, newExcludes: cappedValues });
          }}
          components={{
            Option: CheckboxOption,
            DropdownIndicator: () => <ChevronDown color="#6B7280" />,
            ClearIndicator,
            MultiValueRemove,
          }}
        />
      </div>
    </div>
  );
};

export default CompanyListFilter;
